import { DeleteOutlined, EditOutlined, SendOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Empty,
  Input,
  List,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import {
  deleteProjectTaskComment,
  editProjectTaskComment,
  listProjectMembers,
  listProjectTaskComments,
  postProjectTaskComment,
} from '../../api/projects';
import type { ProjectMember, ProjectTaskComment, ProjectTaskItem } from '../../api/projects';
import { listUserAccounts } from '../../api/userAccounts';
import type { UserAccount } from '../../api/userAccounts';
import { formatDateTime } from './projectDisplay';

/** Số bản ghi nạp cho phép tra tên người viết. */
const lookupPageSize = 200;

/**
 * Hộp thoại trao đổi trong một công việc.
 *
 * <b>Đây là chỗ ngữ cảnh của công việc được giữ lại.</b> Với công ty làm remote toàn bộ, phần lớn
 * thông tin quan trọng nhất của một dự án — vì sao chọn cách này, khách đã đồng ý những gì — nằm
 * trong các cuộc trao đổi. Không có chỗ ghi thì chúng nằm rải rác trong tin nhắn cá nhân, và người
 * vào dự án sau không có cách nào đọc lại.
 *
 * <b>Nút sửa chỉ hiện trên lời của chính mình, nút xóa hiện theo đúng quyền máy chủ trả về.</b> Hai
 * cờ ấy do máy chủ tính chứ không do giao diện tự suy — cài phép suy ở hai nơi thì có ngày hai nơi
 * hiểu khác nhau, và khi đó người dùng thấy một nút bấm vào thì bị từ chối.
 *
 * <b>Bình luận đã sửa luôn mang thẻ đánh dấu.</b> Một lời sửa xong trông y hệt lời gốc thì cả dòng
 * lịch sử mất giá trị làm bằng chứng khi tranh cãi với khách.
 */
export function ProjectTaskCommentModal({
  projectId,
  task,
  open,
  onClose,
}: {
  readonly projectId: string;
  readonly task: ProjectTaskItem | null;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const [comments, setComments] = useState<readonly ProjectTaskComment[] | null>(null);
  const [users, setUsers] = useState<readonly UserAccount[]>([]);
  const [members, setMembers] = useState<readonly ProjectMember[]>([]);
  const [mentionedUserIds, setMentionedUserIds] = useState<readonly string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');

  useEffect(() => {
    if (!open || task === null) {
      return;
    }

    let isCurrent = true;

    async function load(taskId: string): Promise<void> {
      setErrorMessage(null);

      try {
        const [loaded, loadedMembers] = await Promise.all([
          listProjectTaskComments(projectId, taskId),
          listProjectMembers(projectId),
        ]);

        /*
          Tên người viết là thứ CÓ CŨNG ĐƯỢC: danh mục tài khoản đòi một quyền khác với quyền xem
          công việc, nên người chỉ có quyền xem việc vẫn phải đọc được dòng trao đổi.
        */
        let loadedUsers: readonly UserAccount[] = [];

        try {
          const page = await listUserAccounts({ pageSize: lookupPageSize });

          loadedUsers = page.items;
        } catch {
          loadedUsers = [];
        }

        if (!isCurrent) {
          return;
        }

        setComments(loaded);
        setUsers(loadedUsers);
        setMembers(loadedMembers);
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được dòng trao đổi.',
        );
      }
    }

    void load(task.id);

    return () => {
      isCurrent = false;
    };
  }, [projectId, task, open]);

  const reload = useCallback(async (): Promise<void> => {
    if (task === null) {
      return;
    }

    setComments(await listProjectTaskComments(projectId, task.id));
  }, [projectId, task]);

  async function post(): Promise<void> {
    if (task === null || draft.trim().length === 0) {
      return;
    }

    setIsSaving(true);

    try {
      await postProjectTaskComment(projectId, task.id, draft, mentionedUserIds);

      setDraft('');
      setMentionedUserIds([]);
      await reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không gửi được bình luận.');
    } finally {
      setIsSaving(false);
    }
  }

  async function saveEdit(commentId: string): Promise<void> {
    if (task === null) {
      return;
    }

    setIsSaving(true);

    try {
      await editProjectTaskComment(projectId, task.id, commentId, editingBody);

      setEditingId(null);
      setEditingBody('');
      await reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không sửa được bình luận.');
    } finally {
      setIsSaving(false);
    }
  }

  async function remove(commentId: string): Promise<void> {
    if (task === null) {
      return;
    }

    try {
      await deleteProjectTaskComment(projectId, task.id, commentId);

      await reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không xóa được bình luận.');
    }
  }

  /** Tên hiển thị của một người, hoặc chính định danh khi không tra được. */
  function displayName(userAccountId: string): string {
    const user = users.find((candidate) => candidate.id === userAccountId);

    return user === undefined ? userAccountId : user.fullName;
  }

  return (
    <Modal
      open={open}
      title={task === null ? 'Trao đổi' : `Trao đổi — ${task.wbsCode} ${task.name}`}
      width={760}
      footer={null}
      onCancel={onClose}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

        <List<ProjectTaskComment>
          size="small"
          dataSource={(comments ?? []) as ProjectTaskComment[]}
          loading={comments === null && errorMessage === null}
          locale={{
            emptyText: (
              <Empty description="Chưa có trao đổi nào. Những gì bàn ở đây sẽ còn lại cho người vào dự án sau." />
            ),
          }}
          renderItem={(comment) => (
            <List.Item
              actions={[
                ...(comment.isMine
                  ? [
                      <Button
                        key="edit"
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setEditingId(comment.id);
                          setEditingBody(comment.body);
                        }}
                      />,
                    ]
                  : []),
                ...(comment.canDelete
                  ? [
                      <Button
                        key="delete"
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          void remove(comment.id);
                        }}
                      />,
                    ]
                  : []),
              ]}
            >
              <List.Item.Meta
                title={
                  <Space size={8} wrap>
                    <Typography.Text strong>{displayName(comment.authorUserId)}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {formatDateTime(comment.postedAt)}
                    </Typography.Text>
                    {comment.isEdited ? (
                      <Tag color="default">đã sửa {formatDateTime(comment.editedAt)}</Tag>
                    ) : null}
                    {comment.mentionedUserIds.map((userAccountId) => (
                      <Tooltip
                        key={userAccountId}
                        title="Người này đã nhận cảnh báo về bình luận trên."
                      >
                        <Tag color="blue">@{displayName(userAccountId)}</Tag>
                      </Tooltip>
                    ))}
                  </Space>
                }
                description={
                  editingId === comment.id ? (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Input.TextArea
                        value={editingBody}
                        autoSize={{ minRows: 2, maxRows: 8 }}
                        maxLength={4000}
                        onChange={(event) => {
                          setEditingBody(event.target.value);
                        }}
                      />
                      <Space size={8}>
                        <Button
                          size="small"
                          type="primary"
                          loading={isSaving}
                          onClick={() => {
                            void saveEdit(comment.id);
                          }}
                        >
                          Lưu
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setEditingId(null);
                            setEditingBody('');
                          }}
                        >
                          Bỏ qua
                        </Button>
                      </Space>
                    </Space>
                  ) : (
                    <Typography.Paragraph
                      style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                      // Giữ nguyên xuống dòng của người viết: một đoạn kỹ thuật xuống dòng có chủ ý.
                    >
                      {comment.body}
                    </Typography.Paragraph>
                  )
                }
              />
            </List.Item>
          )}
        />

        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {/*
            Chọn người từ danh sách thay vì gõ dấu @ rồi để máy chủ dò chuỗi. Dò chuỗi tìm tên là
            cách sai: tên người đổi được, trùng tên là chuyện thường, và một dấu @ trong địa chỉ thư
            điện tử sẽ bị hiểu nhầm thành lời gọi tên.

            Chỉ hiện thành viên ĐANG tham gia dự án — máy chủ cũng chặn, nhưng hiện cả người khác rồi
            để máy chủ từ chối là cách chắc chắn làm người dùng bực.
          */}
          <Select
            mode="multiple"
            allowClear
            value={mentionedUserIds as string[]}
            placeholder="Nhắc tên ai đó — họ sẽ nhận cảnh báo khi đăng nhập"
            style={{ width: '100%' }}
            maxCount={10}
            optionFilterProp="label"
            options={members
              .filter((member) => member.isActive)
              .map((member) => ({
                value: member.userAccountId,
                label: displayName(member.userAccountId),
              }))}
            onChange={(value) => {
              setMentionedUserIds(value);
            }}
          />
          <Input.TextArea
            value={draft}
            placeholder="Viết trao đổi về công việc này — vì sao chọn cách này, khách đã đồng ý gì…"
            autoSize={{ minRows: 2, maxRows: 8 }}
            maxLength={4000}
            showCount
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={isSaving}
            disabled={draft.trim().length === 0}
            onClick={() => {
              void post();
            }}
          >
            Gửi
          </Button>
        </Space>

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Chỉ người viết mới sửa được lời của mình, và bình luận đã sửa luôn mang dấu <i>đã sửa</i>.
          Người được giao việc thấy cảnh báo về bình luận mới trong bảy ngày gần đây; người được
          nhắc tên thì nhận một cảnh báo riêng ở mức cao hơn, vì bị gọi tên là một lời đề nghị trực
          tiếp chứ không phải tiếng ồn nền.
        </Typography.Text>
      </Space>
    </Modal>
  );
}
